/**
 * MultiDayPicker
 *
 * Wiederverwendbare Komponente zur Mehr-Tage-Auswahl per Klick.
 * Identische UX wie im "Einsatz kopieren"-Dialog.
 *
 * Props:
 *   - selectedDays: string[] (YYYY-MM-DD)
 *   - onChange: (days: string[]) => void
 *   - weekStart: Date          Initialer Wochenstart (Montag) – wird intern gehalten
 *   - excludeDate?: string     Ein Datum, das nicht ausgewählt werden darf (z.B. das Quelldatum)
 */
import React, { useState } from 'react';
import { format, addDays, parseISO, eachDayOfInterval, isWeekend, endOfMonth, startOfWeek, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { DatePickerDE } from './ui/date-picker-de';
import { Calendar, ChevronLeft, ChevronRight, CalendarRange, Plus, Trash2 } from 'lucide-react';
import { formatDateDE } from '../pages/shifts/index';

const getWeekdaysInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (start > end) return [];
  return eachDayOfInterval({ start, end })
    .filter((day) => !isWeekend(day))
    .map((day) => format(day, 'yyyy-MM-dd'));
};

export function MultiDayPicker({ selectedDays = [], onChange, weekStart, excludeDate }) {
  // Auf Wochenstart (Montag) normalisieren, damit das 5-Spalten-Grid immer Mo–Fr zeigt.
  const initialWeekStart = startOfWeek(weekStart || new Date(), { weekStartsOn: 1 });
  const [pickerWeekStart, setPickerWeekStart] = useState(initialWeekStart);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(pickerWeekStart, i));

  const mergeDays = (newDays) => {
    const filtered = newDays.filter((d) => d !== excludeDate);
    onChange([...new Set([...selectedDays, ...filtered])]);
  };

  const toggleDay = (dayStr) => {
    if (dayStr === excludeDate) return;
    onChange(selectedDays.includes(dayStr)
      ? selectedDays.filter((d) => d !== dayStr)
      : [...selectedDays, dayStr]);
  };

  const selectCurrentWeek = () => {
    const days = weekDays.map((d) => format(d, 'yyyy-MM-dd'));
    mergeDays(days);
  };

  const selectNextWeek = () => {
    const wkStart = addDays(pickerWeekStart, 7);
    const days = Array.from({ length: 5 }, (_, i) => format(addDays(wkStart, i), 'yyyy-MM-dd'));
    mergeDays(days);
  };

  const selectNextTwoWeeks = () => {
    const w1 = addDays(pickerWeekStart, 7);
    const w2 = addDays(pickerWeekStart, 14);
    const days = [
      ...Array.from({ length: 5 }, (_, i) => format(addDays(w1, i), 'yyyy-MM-dd')),
      ...Array.from({ length: 5 }, (_, i) => format(addDays(w2, i), 'yyyy-MM-dd')),
    ];
    mergeDays(days);
  };

  const selectUntilMonthEnd = () => {
    const base = pickerWeekStart;
    const monthEnd = endOfMonth(base);
    const days = eachDayOfInterval({ start: base, end: monthEnd })
      .filter((d) => !isWeekend(d))
      .map((d) => format(d, 'yyyy-MM-dd'));
    mergeDays(days);
  };

  const selectFromDateRange = () => {
    if (!dateRange.start || !dateRange.end) return;
    const days = getWeekdaysInRange(dateRange.start, dateRange.end);
    mergeDays(days);
  };

  const clearAll = () => onChange([]);

  return (
    <div className="space-y-4">
      {/* Quick-Buttons */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Schnellauswahl</Label>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={selectCurrentWeek} data-testid="multiday-select-week">
            <Calendar className="h-3 w-3 mr-1" />Diese Woche
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={selectNextWeek} data-testid="multiday-select-nextweek">
            <ChevronRight className="h-3 w-3 mr-1" />Nächste Woche
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={selectNextTwoWeeks} data-testid="multiday-select-2weeks">
            <ChevronRight className="h-3 w-3 mr-1" />Nächste 2 Wochen
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={selectUntilMonthEnd} data-testid="multiday-select-monthend">
            <CalendarRange className="h-3 w-3 mr-1" />Bis Monatsende
          </Button>
          {selectedDays.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={clearAll} className="text-red-500 hover:text-red-700" data-testid="multiday-clear">
              <Trash2 className="h-3 w-3 mr-1" />Auswahl löschen
            </Button>
          )}
        </div>
      </div>

      {/* Date Range */}
      <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
        <Label className="text-sm font-medium">Datumsbereich auswählen</Label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Label className="text-xs text-muted-foreground shrink-0">Von:</Label>
            <DatePickerDE
              value={dateRange.start}
              onChange={(v) => setDateRange((prev) => ({ ...prev, start: v }))}
              placeholder="Von"
              className="flex-1 sm:w-36 sm:flex-none h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Label className="text-xs text-muted-foreground shrink-0">Bis:</Label>
            <DatePickerDE
              value={dateRange.end}
              onChange={(v) => setDateRange((prev) => ({ ...prev, end: v }))}
              placeholder="Bis"
              className="flex-1 sm:w-36 sm:flex-none h-8 text-sm"
            />
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={selectFromDateRange} className="w-full sm:w-auto" data-testid="multiday-range-add">
            <Plus className="h-3 w-3 mr-1" />Hinzufügen
          </Button>
        </div>
        {dateRange.start && dateRange.end && (
          <p className="text-xs text-muted-foreground">
            {getWeekdaysInRange(dateRange.start, dateRange.end).length} Werktage im Bereich
          </p>
        )}
      </div>

      {/* Week navigation */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Wochenauswahl</Label>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPickerWeekStart((p) => addDays(p, -7))} data-testid="multiday-prev-week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[140px] text-center">
              KW {getISOWeek(pickerWeekStart)} • {format(pickerWeekStart, 'dd.MM.', { locale: de })}
            </span>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPickerWeekStart((p) => addDays(p, 7))} data-testid="multiday-next-week">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {weekDays.map((day) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const isSelected = selectedDays.includes(dayStr);
            const isExcluded = dayStr === excludeDate;
            const isPast = format(day, 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd');
            return (
              <button
                key={dayStr}
                type="button"
                onClick={() => toggleDay(dayStr)}
                disabled={isExcluded}
                data-testid={`multiday-day-${dayStr}`}
                className={`p-2 rounded-lg border text-center transition-all ${
                  isExcluded
                    ? 'bg-amber-100 text-amber-700 border-amber-300 cursor-not-allowed'
                    : isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : isPast
                        ? 'bg-muted/30 text-muted-foreground hover:border-primary/30'
                        : 'hover:border-primary/50'
                }`}
              >
                <div className="text-xs">{format(day, 'EEE', { locale: de })}</div>
                <div className="font-medium">{format(day, 'd.')}</div>
                <div className="text-[10px] text-muted-foreground">{format(day, 'MMM', { locale: de })}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      {selectedDays.length > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="multiday-summary">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-800">
              {selectedDays.length} Tag{selectedDays.length > 1 ? 'e' : ''} ausgewählt
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {[...selectedDays].sort().slice(0, 10).map((dayStr) => (
              <Badge key={dayStr} variant="secondary" className="text-xs">
                {formatDateDE(dayStr)}
              </Badge>
            ))}
            {selectedDays.length > 10 && (
              <Badge variant="outline" className="text-xs">+{selectedDays.length - 10} weitere</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiDayPicker;
